import React, { useEffect, useState } from "react";
import {
  FaEye,
  FaEdit,
  FaBan,
  FaDownload,
  FaPlus,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaCheck,
  FaSearch,
} from "react-icons/fa";
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import LocationFormModal from "./LocationFormModal";
import LocationViewModal from "./LocationViewModal";
import Sidebar from "./Sidebar";
import { useScreenPermissions } from "./useScreenPermissions";
import { useNavigate } from "react-router-dom";

// Extend dayjs with UTC plugin
dayjs.extend(utc);

const LeftArrowIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
    <path
      fill="currentColor"
      d="M41.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 256 246.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z"
    />
  </svg>
);

const RightArrowIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
    <path
      fill="currentColor"
      d="M278.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-160 160c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L210.7 256 73.4 118.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l160 160z"
    />
  </svg>
);

const API_URL = "/api";

const LocationMaster = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [locationType, setLocationType] = useState("All");
  const [status, setStatus] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const itemsPerPage = 6;
  
  // --- ADD THESE THREE NEW STATE VARIABLES ---
  const [isSyncing, setIsSyncing] = useState(false);
  const [showSyncSuccess, setShowSyncSuccess] = useState(false);
  const [syncData, setSyncData] = useState(null);

  // Get user permissions
  const { hasPermission, hasFullAccess, loading: permissionsLoading } = useScreenPermissions();

  // --- THEME CSS VARIABLES (match VendorMaster theme) ---
  useEffect(() => {
    const themeVars = {
      "--sidebar-line-color": "#e6efee",
      "--sidebar-bg": "#ffffff",
      "--accent-color": "#2b8f85",
      "--accent-hover": "#23756e",
      "--page-border-color": "#e6efee",
      "--text-primary": "#334155",
      "--text-muted": "#475569",
      "--text-muted-2": "#64748b",
      "--disabled-color": "#bcd8d5",
      "--sort-inactive": "#cddfe0",
      "--search-icon": "#cbd5da",
      "--status-active-bg": "#e6f6f3",
      "--status-active-color": "#0f766e",
      "--status-inactive-bg": "#fafaf9",
      "--status-inactive-color": "#6b7280",
      "--page-bg": "#EEF6F5",
      "--table-header-bg": "#EEF6F5",
      "--surface-1": "#ffffff",
      "--surface-2": "#f6fbfa",
      "--divider": "#e6efee",
      "--danger": "#ef4444",
      "--inactive-text": "#6b7280",
      "--inactive-surface": "#fafaf9",
      "--active-pill-bg": "#e6f6f3",
      "--active-pill-text": "#0f766e",
      "--inactive-pill-bg": "#fafaf9",
      "--inactive-pill-text": "#6b7280"
    };

    Object.entries(themeVars).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value);
    });
  }, []);

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const fetchLocations = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/locations-filter`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      setLocations(Array.isArray(data) ? data : []);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching locations:", error);
      setLocations([]);
    }
    setLoading(false);
  };

    const handleSync = async () => {
    if (isSyncing) return;
    setIsSyncing(true);
    
    try {
      // Make sure this URL matches your actual backend route for Location Sync
      const res = await fetch(`${API_URL}/mapping/bc-locations/sync`, { method: 'GET' ,headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      const data = await res.json();

      if (data.success) {
        setSyncData({
          total: data.recordCount,
          inserted: data.inserted,
          updated: data.updated
        });
        setShowSyncSuccess(true);
        fetchLocations(); // Refresh the list
      } else {
        alert(`Sync Failed: ${data.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Sync Error:", error);
      alert("An error occurred while syncing locations.");
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });
    
    const sortedLocations = [...locations].sort((a, b) => {
      if (a[key] === null) return 1;
      if (b[key] === null) return -1;
      
      if (key === "CreatedAt") {
        return direction === "asc" 
          ? new Date(a[key]) - new Date(b[key])
          : new Date(b[key]) - new Date(a[key]);
      }
      
           if (key === "LocationCode") {
        return direction === "asc"
          ? a[key].localeCompare(b[key], undefined, { numeric: true, sensitivity: 'base' })
          : b[key].localeCompare(a[key], undefined, { numeric: true, sensitivity: 'base' });
      }

            if (key === "Feeder") {
        const valA = (a.Feeder || "").toLowerCase();
        const valB = (b.Feeder || "").toLowerCase();
        if (valA < valB) return direction === "asc" ? -1 : 1;
        if (valA > valB) return direction === "asc" ? 1 : -1;
        return 0;
      }
      
      const av = (a[key] ?? "").toString().toLowerCase();
      const bv = (b[key] ?? "").toString().toLowerCase();
      if (av < bv) return direction === "asc" ? -1 : 1;
      if (av > bv) return direction === "asc" ? 1 : -1;
      return 0;
    });
    
    setLocations(sortedLocations);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ml-1 w-3 h-3 text-slate-400" />;
    return sortConfig.direction === "asc" 
      ? <FaSortUp className="ml-1 w-3 h-3 text-slate-800" /> 
      : <FaSortDown className="ml-1 w-3 h-3 text-slate-800" />;
  };

  const handleView = (location) => {
    setSelectedLocation(location.LocationID);
    setShowView(true);
  };

  const handleEdit = (location) => {
    setSelectedLocation(location.LocationID);
    setIsEdit(true);
    setShowForm(true);
  };

   const handleLogout = () => {
    navigate('/login');
  };

  const handleDeactivate = async (location) => {
    if (!window.confirm("Are you sure you want to deactivate this location?")) return;
    
    try {
      const formData = new FormData();
      formData.append("locationName", location.LocationName);
      formData.append("country", location.Country || "");
      formData.append("state", location.State || "");
      formData.append("city", location.City || "");
      formData.append("postalCode", location.PostalCode || "");
      formData.append("locationTypeID", location.LocationTypeID);
      formData.append("status", "0");
      formData.append("addressLine1", location.AddressLine1 || "");
      formData.append("addressLine2", location.AddressLine2 || "");
      formData.append("parentLocationID", location.ParentLocationID || "");
      formData.append("fileName", location.FileName || "");
      formData.append("fileType", location.FileType || "");
      formData.append("updatedBy", "1");
      
      const response = await fetch(`${API_URL}/location/${location.LocationID}`, {
        method: "PUT",
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: formData,
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to deactivate location");
      }
      
      fetchLocations();
    } catch (error) {
      console.error("Error deactivating location:", error);
      alert(error.message || "An error occurred while deactivating the location");
    }
  };

  const handleReactivate = async (location) => {
    if (!window.confirm("Are you sure you want to reactivate this location?")) return;
    
    try {
      const formData = new FormData();
      formData.append("locationName", location.LocationName);
      formData.append("country", location.Country || "");
      formData.append("state", location.State || "");
      formData.append("city", location.City || "");
      formData.append("postalCode", location.PostalCode || "");
      formData.append("locationTypeID", location.LocationTypeID);
      formData.append("status", "1");
      formData.append("addressLine1", location.AddressLine1 || "");
      formData.append("addressLine2", location.AddressLine2 || "");
      formData.append("parentLocationID", location.ParentLocationID || "");
      formData.append("fileName", location.FileName || "");
      formData.append("fileType", location.FileType || "");
      formData.append("updatedBy", "1");
      
      const response = await fetch(`${API_URL}/location/${location.LocationID}`, {
        method: "PUT",
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: formData,
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to reactivate location");
      }
      
      fetchLocations();
    } catch (error) {
      console.error("Error reactivating location:", error);
      alert(error.message || "An error occurred while reactivating the location");
    }
  };

  const handleDownloadCSV = () => {
    const headers = [
      "LocationCode",
      "LocationName",
      "Country",   
      "City",
      "Feeder",
      "CreatedAt",
    ];
    
    const rows = locations.map((l) => [
      l.LocationCode,
      l.LocationName,
      l.Country,
      l.City,
      l.Feeder,
      dayjs.utc(l.CreatedAt).format("YYYY-MM-DD HH:mm:ss"), // Fixed: Use UTC
    ]);
    
    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "locations.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleCreate = () => {
    setSelectedLocation(null);
    setIsEdit(false);
    setShowForm(true);
  };

  const handleFormSubmit = async (formData) => {
    if (formLoading) return;
    setFormLoading(true);
    
    try {
      if (isEdit && selectedLocation) {
        const response = await fetch(`${API_URL}/location/${selectedLocation}`, {
          method: "PUT",
          headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
          body: formData,
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to update location");
        }
      } else {
        const response = await fetch(`${API_URL}/location`, {
          method: "POST",
          headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
          body: formData,
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          if (errorData.message && errorData.message.includes("duplicate key")) {
            setShowForm(false);
            setTimeout(() => {
              setShowForm(true);
              alert("Duplicate location code detected. The form has been refreshed with a new location code. Please try again.");
            }, 100);
            return;
          }
          throw new Error(errorData.message || "Failed to create location");
        }
      }
      
      setShowForm(false);
      fetchLocations();
    } catch (error) {
      console.error("Error saving location:", error);
      alert(error.message || "An error occurred while saving the location");
    } finally {
      setFormLoading(false);
    }
  };

 
   const filteredLocations = locations.filter((location) => {
    // Convert searchTerm to lowercase once for efficiency
    const lowerSearchTerm = searchTerm.toLowerCase();

    const matchesSearch = searchTerm === "" ||
      location.LocationCode.toLowerCase().includes(lowerSearchTerm) ||
      location.LocationName.toLowerCase().includes(lowerSearchTerm) ||
      (location.Country && location.Country.toLowerCase().includes(lowerSearchTerm)) ||
      (location.State && location.State.toLowerCase().includes(lowerSearchTerm)) ||
      (location.City && location.City.toLowerCase().includes(lowerSearchTerm)) ||
      // --- FEEDER SEARCH ADDED HERE ---
      (location.Feeder && location.Feeder.toLowerCase().includes(lowerSearchTerm));

    const matchesLocationType = locationType === "All" || location.LocationTypeName === locationType;
    const matchesStatus = status === "All" || 
      (status === "Active" && location.Status) || 
      (status === "Inactive" && !location.Status);
    
    return matchesSearch && matchesLocationType && matchesStatus;
  });

  const totalPages = Math.ceil(filteredLocations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const locationsForPage = filteredLocations.slice(startIndex, endIndex);

  const handlePageChange = (page) => setCurrentPage(page);

  const renderPaginationButtons = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);
    
    pages.push(
      <button
        key="prev"
        className="p-1 rounded-md text-slate-800 hover:bg-slate-100 disabled:text-slate-300 disabled:cursor-not-allowed"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <LeftArrowIcon className="w-3 h-3" />
      </button>
    );
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`px-4 py-1  transition-colors ${
            currentPage === i
              ? "bg-teal-600 text-white font-semibold"
              : "text-slate-800 hover:bg-slate-100"
          }`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }
    
    pages.push(
      <button
        key="next"
        className="p-1 rounded-md text-slate-800 hover:bg-slate-100 disabled:text-slate-300 disabled:cursor-not-allowed"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages || totalPages === 0}
      >
        <RightArrowIcon className="w-3 h-3" />
      </button>
    );
    
    return pages;
  };

  const locationTypes = [
    ...new Set(locations.map((l) => l.LocationTypeName).filter(Boolean)),
  ];

  // Check permissions for actions
  const canCreate = hasFullAccess || hasPermission('WAREHOUSE', 'CanCreate');
  const canView = hasFullAccess || hasPermission('WAREHOUSE', 'CanView');
  const canEdit = hasFullAccess || hasPermission('WAREHOUSE', 'CanEdit');
  const canDelete = hasFullAccess || hasPermission('WAREHOUSE', 'CanDelete');

  if (permissionsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--page-bg)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 mx-auto" style={{ borderColor: 'var(--accent-color)' }}></div>
          <p className="mt-4" style={{ color: 'var(--text-muted)' }}>Loading permissions...</p>
        </div>
      </div>
    );
  }

  // Theme object passed to Sidebar so it matches this page's accent/profile color
  const sidebarTheme = {
    text: 'var(--text-primary, #334155)',
    muted: 'var(--text-muted, #475569)',
    border: 'var(--page-border-color, #e6efee)',
    sidebarBorder: 'var(--sidebar-line-color, #e6efee)',
    background: 'var(--sidebar-bg, #ffffff)',
    pageBackground: 'var(--page-bg, #EEF6F5)',
    activeBg: 'var(--accent-color, #2b8f85)',
    profileBg: 'var(--accent-color, #2b8f85)',
    activeText: '#ffffff',
    accent: 'var(--accent-color, #2b8f85)',
    accentHover: 'var(--accent-hover, #23756e)',
    hoverGray: 'rgba(0,0,0,0.04)',
    iconColor: 'var(--accent-color, #2b8f85)',
    sidebarBg: 'var(--sidebar-bg, #ffffff)',
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--page-bg)', color: 'var(--text-primary)' }}>
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} theme={sidebarTheme} 
      onLogout={handleLogout}/>
      <main className={`flex-1 p-6 lg:p-8 overflow-hidden transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}
`}>
        {/* Header */}
        <header className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-gray-300 pb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-1">Warehouse</h1>
            <p className="text-gray-600">Manage your business locations and facilities</p>
          </div>
          <div className="flex items-center gap-3">
            {/* --- ADD SYNC BUTTON HERE --- */}
            <button 
              onClick={handleSync} 
              disabled={isSyncing}
              className={`flex items-center gap-2 px-5 py-2 font-medium transition-all duration-200 shadow-sm border ${
                isSyncing 
                  ? 'bg-gray-100 text-gray-500 cursor-not-allowed' 
                  : 'bg-white text-blue-600 border-blue-200 hover:bg-blue-50'
              }`}
            >
              {isSyncing ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Syncing...
                </>
              ) : (
                <>
                  <FaCheck className="h-4 w-4" />
                  Sync 
                </>
              )}
            </button>
            <button
              className="flex items-center gap-2 px-4 py-2"
              onClick={handleDownloadCSV}
              style={{ backgroundColor: 'var(--surface-1)', color: 'var(--accent-color)', border: `1px solid var(--divider)` }}
            >
              <FaDownload className="h-4 w-4" />
              Export
            </button>
            {/* {canCreate && (
              <button
                onClick={handleCreate}
                className="flex items-center gap-2 px-4 py-2 font-medium rounded-none"
                style={{ backgroundColor: 'var(--accent-color)', color: '#ffffff' }}
              >
                <FaPlus className="h-4 w-4" />
                Create New
              </button>
            )} */}
          </div>
        </header>

        {/* Stats */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="p-5 bg-white border border-gray-300 shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-600">Total Locations</p>
                <p className="text-2xl font-bold text-gray-900">{locations.length}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center bg-gray-100 text-gray-800">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white border border-gray-300 shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-600">Active Locations</p>
                <p className="text-2xl font-bold text-gray-900">{locations.filter((l) => l.Status).length}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: 'var(--active-pill-bg)', color: 'var(--active-pill-text)' }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white border border-gray-300 shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-600">Inactive Locations</p>
                <p className="text-2xl font-bold text-gray-900">{locations.filter((l) => !l.Status).length}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: 'var(--inactive-surface)', color: 'var(--inactive-pill-text)' }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white border border-gray-300 shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-600">This Month</p>
                <p className="text-2xl font-bold text-gray-900">{locations.filter((l) => {
                    const createdDate = new Date(l.CreatedAt);
                    const now = new Date();
                    return (
                      createdDate.getMonth() === now.getMonth() &&
                      createdDate.getFullYear() === now.getFullYear()
                    );
                  }).length}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center bg-gray-100 text-gray-800">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>
        </section>

        {/* Search & Filters - SHARP EDGES */}
        <section className="p-5 bg-white border border-gray-300 shadow-md mb-6" style={{ borderRadius: 0 }}>
          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Search Locations</label>
              <div className="relative">
                {/* make icon pointer-events-none and add left padding to input so text doesn't overlap */}
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search by code, name, city..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  aria-label="Search locations"
                  className="w-full pl-10 pr-4 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-color)] transition-all duration-200"
                />
              </div>
            </div>

            {/* <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Location Type</label>
              <select
                value={locationType}
                onChange={(e) => setLocationType(e.target.value)}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-color)] transition-all duration-200"
              >
                <option>All</option>
                {locationTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div> */}

            {/* <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-color)] transition-all duration-200"
              >
                <option>All</option>
                <option>Active</option>
                <option>Inactive</option>
              </select>
            </div> */}

            <button className="px-5 py-2 rounded-none" onClick={() => setCurrentPage(1)} style={{ backgroundColor: 'var(--accent-color)', color: '#ffffff' }}>
              Search
            </button>
          </div>
        </section>

        {/* Table - SHARP EDGES */}
        <section className="bg-white border border-gray-300 shadow-md" style={{ borderRadius: 0 }}>
          <div className="flex justify-between items-center p-4 border-b border-gray-300">
            <h2 className="text-xl font-semibold text-gray-900">Location Summary</h2>
            <div className="flex gap-2 items-center">
              <div className="flex items-center gap-2 text-gray-700">{renderPaginationButtons()}</div>
            </div>
          </div>

          {loading ? (
            <div className="p-8 text-center text-gray-600">Loading locations...</div>
          ) : locationsForPage.length === 0 ? (
            <div className="p-8 text-center text-gray-600">No locations found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead style={{ backgroundColor: 'var(--table-header-bg)', color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: 12, fontWeight: 600 }}>
                  <tr>
                    <th className={`px-3 py-3 text-left cursor-pointer ${isScrolled ? 'shadow' : ''}`} onClick={() => handleSort("LocationCode")}>
                      <div className="flex items-center">LOCATION CODE {getSortIcon("LocationCode")}</div>
                    </th>
                    <th className={`px-4 py-3 text-left cursor-pointer ${isScrolled ? 'shadow' : ''}`} onClick={() => handleSort("LocationName")}>
                      <div className="flex items-center">LOCATION NAME {getSortIcon("LocationName")}</div>
                    </th>
                    <th className={`px-4 py-3 text-left cursor-pointer ${isScrolled ? 'shadow' : ''}`} onClick={() => handleSort("Country")}>
                      <div className="flex items-center">COUNTRY {getSortIcon("Country")}</div>
                    </th>
                    {/* <th className={`px-3 py-3 text-left cursor-pointer ${isScrolled ? 'shadow' : ''}`} onClick={() => handleSort("State")}>
                      <div className="flex items-center text-center">STATE {getSortIcon("State")}</div>
                    </th> */}
                    <th className={`px-4 py-3 text-left cursor-pointer ${isScrolled ? 'shadow' : ''}`} onClick={() => handleSort("City")}>
                      <div className="flex items-center">CITY {getSortIcon("City")}</div>
                    </th>
                    <th className={`px-4 py-3 text-left cursor-pointer ${isScrolled ? 'shadow' : ''}`} onClick={() => handleSort("Feeder")}>
                      <div className="flex items-center">FEEDER {getSortIcon("Feeder")}</div>
                    </th>
                    {/* <th className={`px-3 py-3 text-left cursor-pointer ${isScrolled ? 'shadow' : ''}`} onClick={() => handleSort("LocationTypeName")}>
                      <div className="flex items-center">LOCATION TYPE {getSortIcon("LocationTypeName")}</div>
                    </th> */}
                    {/* <th className={`px-4 py-3 text-left cursor-pointer ${isScrolled ? 'shadow' : ''}`} onClick={() => handleSort("Status")}>
                      <div className="flex items-center">STATUS {getSortIcon("Status")}</div>
                    </th>
                    <th className={`px-3 py-3 text-left cursor-pointer ${isScrolled ? 'shadow' : ''}`} onClick={() => handleSort("CreatedAt")}>
                      <div className="flex items-center">CREATED AT {getSortIcon("CreatedAt")}</div>
                    </th> */}
                    <th className={`px-3 py-3 text-center`}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 text-left">
                  {locationsForPage.map((location, idx) => (
                    <tr key={location.LocationID || idx} className={`hover:bg-slate-50 transition-colors duration-200 bg-white`}>
                      <td className="px-3 py-3 font-mono text-gray-900 whitespace-nowrap">
                        <p className={`transition-all duration-200 hover:underline cursor-pointer ${!canView ? 'pointer-events-none' : ''}`} onClick={() => canView && handleView(location)}>
                          {location.LocationCode?.toUpperCase() || ""}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-gray-900 whitespace-nowrap font-semibold">{location.LocationName?.toUpperCase() || ""}</td>
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{location.Country?.toUpperCase() || "-"}</td>
                      {/* <td className="px-3 py-3 text-gray-700 whitespace-nowrap">{location.State?.toUpperCase() || "-"}</td> */}
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{location.City?.toUpperCase() || "-"}</td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{location.Feeder?.toUpperCase() || "-"}</td>
                      {/* <td className="px-3 py-3 text-gray-700 whitespace-nowrap">{location.LocationTypeName?.toUpperCase() || "-"}</td> */}
                      {/* <td className="px-4 py-3">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium transition-colors duration-200 ${location.Status ? 'bg-teal-50 text-teal-700 hover:bg-teal-100' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'}`}>
                          {location.Status ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-gray-700 whitespace-nowrap">
                        {location.CreatedAt ? (
                          <div className="flex flex-col leading-tight">
                            <span>{dayjs.utc(location.CreatedAt).format("DD MMM YYYY")}</span>
                            <span className="text-xs text-gray-500">{dayjs.utc(location.CreatedAt).format("HH:mm:ss")}</span>
                          </div>
                        ) : "-"}
                      </td> */}
                      <td className="px-3 py-3 flex gap-2 justify-center items-center">
                        {canView && (
                          <button title="View" className="p-1 rounded-full text-gray-800 hover:text-gray-900 transition-colors duration-200" onClick={() => handleView(location)}>
                            <FaEye />
                          </button>
                        )}
                        {/* {canEdit && (
                          <button title="Edit" className="p-1 rounded-full text-teal-700 hover:text-teal-900 transition-colors duration-200" onClick={() => handleEdit(location)}>
                            <FaEdit />
                          </button>
                        )}
                        {canDelete && (
                          <>
                            {location.Status ? (
                              <button title="Deactivate" className="p-1 rounded-full text-rose-600 hover:text-rose-800 transition-colors duration-200" onClick={() => handleDeactivate(location)}>
                                <FaBan />
                              </button>
                            ) : (
                              <button title="Reactivate" className="p-1 rounded-full text-teal-700 hover:text-teal-900 transition-colors duration-200" onClick={() => handleReactivate(location)}>
                                <FaCheck />
                              </button>
                            )}
                          </>
                        )} */}
                        {/* <button title="Download CSV" className="p-2 rounded-full text-gray-700 hover:text-gray-900 transition-colors duration-200" onClick={handleDownloadCSV}>
                          <FaDownload />
                        </button> */}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Modals */}
        <LocationFormModal
          open={showForm}
          onClose={() => setShowForm(false)}
          onSubmit={handleFormSubmit}
          initialData={selectedLocation}
          isEdit={isEdit}
          loading={formLoading}
        />
        
        <LocationViewModal
          open={showView}
          onClose={() => setShowView(false)}
           locationId={selectedLocation}
        />

        
        {/* --- ADD SYNC SUCCESS MODAL HERE --- */}
        {showSyncSuccess && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6 text-center transform transition-all scale-100">
              {/* Success Icon */}
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <FaCheck className="h-8 w-8 text-green-600" />
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-2">Sync Complete!</h3>
              <p className="text-gray-500 mb-6">The location data has been successfully synchronized.</p>

              {/* <div className="grid grid-cols-3 gap-4 mb-6 text-center">
                 <div>
                   <span className="block font-bold text-gray-900">{syncData?.total || 0}</span>
                   <span className="text-xs text-gray-500 uppercase">Total</span>
                 </div>
                 <div>
                   <span className="block font-bold text-green-600">{syncData?.inserted || 0}</span>
                   <span className="text-xs text-gray-500 uppercase">Inserted</span>
                 </div>
                 <div>
                   <span className="block font-bold text-blue-600">{syncData?.updated || 0}</span>
                   <span className="text-xs text-gray-500 uppercase">Updated</span>
                 </div>
              </div> */}

              <button
                onClick={() => setShowSyncSuccess(false)}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white primary-btn shadow-sm hover:shadow focus:outline-none transition-all duration-200"
              >
                Close
              </button>
            </div>
          </div>
        )}

      </main>
    </div>
  );
};

export default LocationMaster;
